import * as THREE from 'three'
import { useMapCoordinateStore } from '@/stores/mapCoordinateStore.ts'
import { useMapStore } from '@/stores/mapStore.ts'
import { useMapSettingsStore } from '@/stores/mapSettingsStore.ts'
import { zoomFactor } from '@/enums/zoomFactor.ts'

interface CircleInput {
  x: number
  y: number
  radius: number
}

interface ComputeResponse {
  type: 'result'
  points: Float32Array
  arcOffsets: Uint32Array
  arcLengths: Uint32Array
}

export class MapScanRadius {
  private scene: THREE.Scene
  private scanCircles: THREE.Line[] = []
  private readonly mapCoordinateStore = useMapCoordinateStore()
  private readonly mapStore = useMapStore()
  private readonly mapSettingsStore = useMapSettingsStore()

  // Cache for tracking coordinates with scan ranges
  private scanRangeCoordKeys: Set<string> = new Set()
  private lastCoordinateData: Map<string, { x: number; y: number; range: number }> = new Map()

  // Hash-based change detection - skip redraw if nothing changed
  private lastCacheHash: string = ''

  // Web Worker for computation
  private worker: Worker | null = null
  private pendingComputation: boolean = false

  // Reusable material (created once)
  private readonly lineMaterial: THREE.LineDashedMaterial

  constructor(scene: THREE.Scene) {
    this.scene = scene

    // Create material once and reuse
    this.lineMaterial = new THREE.LineDashedMaterial({
      color: 0x808080,
      transparent: true,
      opacity: 0.7,
      dashSize: 1,
      gapSize: 1
    })

    // Initialize web worker
    this.initWorker()

    this.updateScanRadii()
  }

  /* ────────────────────────── WORKER MANAGEMENT ────────────────────────── */

  private initWorker(): void {
    try {
      this.worker = new Worker(
        new URL('@/workers/ScanRadiusWorker.ts', import.meta.url),
        { type: 'module' }
      )

      this.worker.onmessage = (event: MessageEvent<ComputeResponse>) => {
        this.handleWorkerResponse(event.data)
      }

      this.worker.onerror = (error) => {
        console.error('ScanRadiusWorker error:', error)
        this.pendingComputation = false
      }
    } catch (error) {
      console.error('Failed to initialize ScanRadiusWorker:', error)
      this.worker = null
    }
  }

  private handleWorkerResponse(response: ComputeResponse): void {
    this.pendingComputation = false

    if (response.type !== 'result') return

    // Clear existing circles before adding new ones
    this.clearCirclesOnly()

    const { points, arcOffsets, arcLengths } = response

    // Create Three.js geometry for each arc/circle
    for (let i = 0; i < arcOffsets.length; i++) {
      const offset = arcOffsets[i] * 3 // Convert point index to float index
      const length = arcLengths[i]

      // Extract points for this arc
      const arcPoints: THREE.Vector3[] = []
      for (let j = 0; j < length; j++) {
        const idx = offset + j * 3
        arcPoints.push(new THREE.Vector3(
          points[idx],
          points[idx + 1],
          points[idx + 2]
        ))
      }

      const geometry = new THREE.BufferGeometry().setFromPoints(arcPoints)
      const line = new THREE.Line(geometry, this.lineMaterial)
      line.name = 'scanRadius'
      this.scanCircles.push(line)
      this.scene.add(line)
      line.computeLineDistances()
    }
  }

  /* ────────────────────────── PUBLIC API ────────────────────────── */

  /** Re-draw scan radii */
  public updateScanRadii(): void {
    this.performUpdate()
  }

  /** Perform the actual update logic */
  private performUpdate(): void {
    // If scan ranges are disabled, clear any existing circles and return
    if (!this.mapSettingsStore.showScanRange) {
      this.clearScanRadii()
      return
    }

    // Hide scan range at quadrant zoom level and up (most zoomed out)
    if (this.mapStore.zoomFactor <= zoomFactor.Quadrant) {
      this.scanCircles.forEach((circle) => {
        circle.visible = false
      })
      return
    }

    // Show circles if they exist but are hidden
    if (this.scanCircles.length > 0) {
      this.scanCircles.forEach((circle) => {
        circle.visible = true
      })
    }

    // Rebuild cache from store (only on full update)
    this.rebuildCache()
    this.requestWorkerComputation()
  }

  /** Rebuild the scan range coordinate cache from store */
  private rebuildCache(): void {
    this.scanRangeCoordKeys.clear()
    this.lastCoordinateData.clear()

    this.mapCoordinateStore.coordinates.forEach((d: any, key: string) => {
      if (d.mapCoordinate?.maxScanRange > 1) {
        this.scanRangeCoordKeys.add(key)
        this.lastCoordinateData.set(key, {
          x: d.mapCoordinate.xPos,
          y: d.mapCoordinate.yPos,
          range: d.mapCoordinate.maxScanRange
        })
      }
    })
  }

  /** Compute a simple hash of cache data for change detection */
  private computeCacheHash(): string {
    if (this.lastCoordinateData.size === 0) return ''
    const parts: string[] = []
    this.lastCoordinateData.forEach((data, key) => {
      parts.push(`${key}:${data.range}`)
    })
    return parts.join(',')
  }

  /** Get segment count based on zoom level (LOD) */
  private getSegmentCount(): number {
    const zoom = this.mapStore.zoomFactor
    if (zoom <= zoomFactor.Sector) return 16  // Far zoom: fewer segments
    if (zoom <= zoomFactor.Local) return 32   // Medium zoom
    return 64  // Close zoom: full detail
  }

  /** Request computation from worker */
  private requestWorkerComputation(): void {
    if (this.scanRangeCoordKeys.size === 0) {
      this.clearCirclesOnly()
      return
    }

    // Hash-based change detection - skip if nothing changed
    const currentHash = this.computeCacheHash()
    if (currentHash === this.lastCacheHash && this.scanCircles.length > 0) {
      return // Nothing changed, skip computation
    }
    this.lastCacheHash = currentHash

    // If no worker available, fall back to synchronous (shouldn't happen)
    if (!this.worker) {
      console.warn('ScanRadiusWorker not available')
      return
    }

    // Don't queue multiple computations
    if (this.pendingComputation) {
      return
    }

    // Convert cache to circle input array
    const circles: CircleInput[] = []
    this.lastCoordinateData.forEach((data) => {
      circles.push({
        x: data.x,
        y: data.y,
        radius: data.range
      })
    })

    this.pendingComputation = true
    this.worker.postMessage({
      type: 'compute',
      circles,
      segmentCount: this.getSegmentCount()
    })
  }

  /** Clear only the circles (not the cache) */
  private clearCirclesOnly(): void {
    this.scanCircles.forEach((obj) => {
      this.scene.remove(obj)
      obj.geometry.dispose()
      // Don't dispose material - we're reusing it
    })
    this.scanCircles = []
  }

  /** Clear meshes *and* free GPU buffers */
  public clearScanRadii(): void {
    this.clearCirclesOnly()
    this.scanRangeCoordKeys.clear()
    this.lastCoordinateData.clear()
  }

  /** Update visibility based on zoom level */
  public applyZoomFactor(): void {
    // Hide at quadrant zoom level and up (more zoomed out), show otherwise
    const shouldBeVisible = this.mapStore.zoomFactor > zoomFactor.Quadrant
    this.scanCircles.forEach((circle) => {
      circle.visible = shouldBeVisible
    })
  }

  /** Dispose of all resources */
  public dispose(): void {
    this.clearScanRadii()
    this.lineMaterial.dispose()

    // Terminate worker
    if (this.worker) {
      this.worker.terminate()
      this.worker = null
    }
  }
}
